import { useSyncExternalStore } from 'react';
import type { FolderEntry } from '@xphoto/shared';
import { fetchAllFolders } from './organize-api';

/**
 * ARBRE COMPLET DES DOSSIERS de la bibliothèque, PARTAGÉ entre les modales qui en ont
 * besoin (« Déplacer vers un dossier », « Téléverser »).
 *
 * ⚠ PERF (audit A9) : chaque modale chargeait l'arbre ENTIER à son montage, sans cache ni
 * partage — ouvrir « Déplacer », annuler, rouvrir coûtait deux listings complets, et une
 * bibliothèque profonde en fait un listing lourd. Ici le résultat SURVIT à la fermeture de
 * la modale : la réouverture est instantanée et muette.
 *
 * Même patron que `CountsSource` (`gallery/use-facets.ts`) : store module-level lu par
 * `useSyncExternalStore`. UNE différence assumée : on NE remet PAS l'état à zéro quand le
 * dernier abonné se démonte — c'est précisément ce qui rend la réouverture gratuite. La
 * fraîcheur est donc pilotée EXPLICITEMENT par `invalidateAllFolders()`, appelée par les
 * actions de la galerie qui changent l'arborescence (mêmes actions que la clé de
 * rafraîchissement des DOSSIERS, cf. `GalleryView`).
 */

export type AllFoldersState =
  { status: 'loading' } | { status: 'error' } | { status: 'ready'; entries: FolderEntry[] };

const LOADING: AllFoldersState = { status: 'loading' };

class AllFoldersSource {
  private state: AllFoldersState = LOADING;
  private readonly listeners = new Set<() => void>();
  private loaded = false;

  constructor(private readonly libraryId: string) {}

  getSnapshot = (): AllFoldersState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    if (!this.loaded) {
      this.loaded = true;
      this.fetch();
    }
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** Marque la liste PÉRIMÉE : la prochaine ouverture de modale la rechargera. */
  invalidate(): void {
    this.loaded = false;
    // Des abonnés sont encore là (modale ouverte pendant l'action) → on recharge tout de
    // suite pour qu'ils voient l'arborescence à jour.
    if (this.listeners.size > 0) {
      this.loaded = true;
      this.fetch();
    }
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }

  private fetch(): void {
    // Conserve la liste précédente pendant un rechargement uniquement si l'on n'a jamais
    // rien eu : sinon un arbre périmé resterait sélectionnable. `loading` est bref.
    this.state = LOADING;
    this.emit();
    fetchAllFolders(this.libraryId)
      .then((entries) => {
        this.state = { status: 'ready', entries };
        this.emit();
      })
      .catch(() => {
        // Échec : la liste redevient chargeable (réessai en rouvrant la modale).
        this.loaded = false;
        this.state = { status: 'error' };
        this.emit();
      });
  }
}

const sources = new Map<string, AllFoldersSource>();

function getSource(libraryId: string): AllFoldersSource {
  let source = sources.get(libraryId);
  if (!source) {
    source = new AllFoldersSource(libraryId);
    sources.set(libraryId, source);
  }
  return source;
}

/**
 * Invalide l'arbre partagé de TOUTES les bibliothèques. Appelé par les actions qui
 * changent l'arborescence (déplacement, téléversement, corbeille, fin de scan) — la
 * granularité par bibliothèque serait du zèle : il y en a une seule par utilisateur, et
 * sur-invalider ici ne coûte qu'un listing à la prochaine ouverture de modale.
 */
export function invalidateAllFolders(): void {
  for (const source of sources.values()) source.invalidate();
}

/** Vide complètement le registre (tests). */
export function resetAllFoldersSources(): void {
  sources.clear();
}

/** Arbre complet des dossiers de `libraryId`, partagé entre appelants. */
export function useAllFolders(libraryId: string): AllFoldersState {
  const source = getSource(libraryId);
  return useSyncExternalStore(source.subscribe, source.getSnapshot, source.getSnapshot);
}
